import React, { useState, useEffect, useRef, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-date-fns";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  zoomPlugin
);

const ProfitAndLossGraph = ({ data }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const chartRef = useRef(null);

  useEffect(() => {
    if (data && data.length > 0) {
      const firstTime = new Date(data[0].ETime);
      const oneHourLater = new Date(firstTime.getTime() + 60 * 60 * 1000);
      const initialFilteredData = data.filter((item) => {
        const itemTime = new Date(item.ETime);
        return itemTime >= firstTime && itemTime <= oneHourLater;
      });
      setFilteredData(initialFilteredData.length > 0 ? initialFilteredData : data);
      setOriginalData(data);
    }
  }, [data]);

  const resetZoom = () => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
    }
    setFilteredData(originalData);
  };

  const chartData = {
    labels: filteredData.map((item) => new Date(item.ETime)),
    datasets: [
      {
        label: "Profit & Loss",
        data: filteredData.map((item) => item?.PnL || 0),
        backgroundColor: filteredData.map((item) =>
          item?.PnL > 0 ? "#66BB6A" : "#EF5350"
        ),
        borderColor: "#ffffff",
        borderWidth: 2,
        barThickness: 18,
        borderRadius: 8,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      zoom: {
        pan: { enabled: true, mode: "xy" },
        zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: "xy" },
      },
      tooltip: {
        backgroundColor: "#5e5c5c",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "#555",
        borderWidth: 1,
      },
      legend: {
        labels: {
          font: { family: "Roboto, sans-serif", size: 14 },
          color: "#555",
        },
      },
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: "minute",
          tooltipFormat: "yyyy-MM-dd hh:mm a",
          displayFormats: {
            minute: "hh:mm a",
            hour: "MMM d, hh:mm a",
            day: "MMM d",
          },
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 10,
          color: "#555",
        },
        grid: { color: "#ccc" },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#ccc" },
        ticks: { color: "#555" },
      },
    },
  };

  // ⚡️ Calculate dynamic width based on number of data points
  const chartWidth = Math.max(filteredData.length * 30, 3000); 

  return (
    <div style={{ position: "relative", height: "500px", width: "100%" }}>
      <button
        onClick={resetZoom}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 2,
          padding: "6px 14px",
          background: "#2196f3",
          color: "#fff",
          border: "none",
          borderRadius: 4,
          cursor: "pointer",
          fontSize: 13,
        }}
      >
        Reset Zoom
      </button>

      <div
        style={{
          overflowX: "auto", // 👈 Horizontal scroll
          overflowY: "hidden",
          width: "100%",
          height: "100%",
        }}
      >
        <div style={{ width: `${chartWidth}px`, height: "100%" }}>
          {filteredData.length > 0 ? (
            <Bar ref={chartRef} data={chartData} options={chartOptions} />
          ) : (
            <p style={{ textAlign: "center", color: "red", fontSize: "16px" }}>
              No data available to display
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfitAndLossGraph;
